import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type RefObject } from "react";
import { backgroundPercent, panRecordingBackground, type BackgroundFrame } from "./recording-background";
import { recordingPositionKey } from "./recording-position";

export function useRecordingBackground(key: string | null, defaults: BackgroundFrame, editable: boolean, stage: RefObject<HTMLElement | null>) {
  const [custom, setCustom] = useState<BackgroundFrame | null>(null);
  const value = custom ?? defaults;
  const drag = useRef<{ id: number; x: number; y: number; frame: BackgroundFrame } | null>(null);
  useEffect(() => { setCustom(null); drag.current = null; }, [key]);
  useEffect(() => { if (!editable) drag.current = null; }, [editable]);
  return { value, changed: custom !== null, reset: () => setCustom(null), zoom: (zoom: number) => setCustom({ ...value, zoom: Math.max(100, Math.min(200, zoom)) }),
    bind: {
      role: "group", "aria-label": "Position background", tabIndex: 0,
      onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
        if (!editable || !event.isPrimary || event.button !== 0) return;
        event.preventDefault(); event.currentTarget.focus({ preventScroll: true });
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, frame: value };
      },
      onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
        const active = drag.current, frame = stage.current;
        const image = frame?.querySelector<HTMLImageElement>("img.stage-backdrop.is-ready");
        if (!editable || !active || active.id !== event.pointerId || !frame || !image) return;
        setCustom(panRecordingBackground(active.frame, { x: event.clientX - active.x, y: event.clientY - active.y },
          { width: frame.clientWidth, height: frame.clientHeight }, { width: image.naturalWidth, height: image.naturalHeight }));
      },
      onPointerUp: (event: PointerEvent<HTMLDivElement>) => {
        if (drag.current?.id !== event.pointerId) return;
        drag.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
      },
      onPointerCancel: () => { drag.current = null; }, onLostPointerCapture: () => { drag.current = null; },
      onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
        if (!editable || event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === "Home") { event.preventDefault(); setCustom(null); return; }
        const delta = recordingPositionKey(event.key, event.shiftKey);
        if (!delta) return;
        event.preventDefault(); setCustom({ ...value, x: backgroundPercent(value.x - delta.x * 100), y: backgroundPercent(value.y - delta.y * 100) });
      },
    } };
}
